//TODO : Refactor after ai
import type { HTMLAttributes } from "react";
import type { OverviewGroup, OverviewObject } from "./types";
import { Attribute } from "./Attribute";
import { Badge, toneOf } from "./primitives";

export interface ObjectKanbanProps extends HTMLAttributes<HTMLDivElement> {
  items?: OverviewObject[];
  groups?: OverviewGroup[];
  groupBy?: string;
  onOpen?: (item: OverviewObject) => void;
  onAdd?: (group: string) => void;
  showAttributes?: number;
  emptyLabel?: string;
}

const groupValue = (item: OverviewObject, key: string): string | undefined => {
  const raw = item[key];
  return typeof raw === "string" ? raw : undefined;
};

/** Kanban representation: objects grouped into columns by one attribute. */
export function ObjectKanban({
  items = [],
  groups = [],
  groupBy = "group",
  onOpen,
  onAdd,
  showAttributes = 1,
  emptyLabel = "Nichts hier",
  className = "",
  ...rest
}: ObjectKanbanProps) {
  const columns: OverviewGroup[] = groups.length
    ? groups
    : [...new Set(items.map((i) => groupValue(i, groupBy)).filter(Boolean))].map((g) => ({
        label: g as string,
        value: g as string,
      }));

  return (
    <div className={`bui-ov-kanban ${className}`} {...rest}>
      {columns.map((group) => {
        const value = group.value ?? group.label;
        const rows = items.filter((i) => groupValue(i, groupBy) === value);
        return (
          <section key={value} className="bui-ov-kanban__col">
            <header className="bui-ov-kanban__head">
              {group.color && (
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: toneOf(group.color).solid,
                    flexShrink: 0,
                  }}
                />
              )}
              <span className="bui-ov-kanban__title">{group.label}</span>
              <span className="bui-obj-list__count">{rows.length}</span>
              {onAdd && (
                <button
                  type="button"
                  aria-label={`Zu ${group.label} hinzufügen`}
                  onClick={() => onAdd(value)}
                  style={{
                    marginLeft: "auto",
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--icon-tertiary)",
                    padding: 2,
                    display: "grid",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              )}
            </header>
            {rows.map((item) => (
              <article
                key={item.id}
                className="bui-ov-kanban__card"
                onClick={() => onOpen?.(item)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="bui-obj-id" style={{ fontSize: 12 }}>
                    {item.identifier}
                  </span>
                  {item.status && (
                    <span style={{ marginLeft: "auto" }}>
                      <Badge color={item.statusColor} dot size="sm">
                        {item.status}
                      </Badge>
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: "var(--text-primary)",
                    textWrap: "pretty",
                  }}
                >
                  {item.title}
                </span>
                {item.subtitle && (
                  <span
                    style={{
                      font: "var(--t-caption-l)",
                      color: "var(--text-tertiary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.subtitle}
                  </span>
                )}
                {showAttributes > 0 &&
                  (item.attributes ?? []).slice(0, showAttributes).map((a) => (
                    <Attribute key={a.label} label={a.label} value={a.value} />
                  ))}
                {item.avatar && <div style={{ marginTop: 2 }}>{item.avatar}</div>}
              </article>
            ))}
            {rows.length === 0 && (
              <p
                style={{
                  font: "var(--t-caption-l)",
                  color: "var(--text-tertiary)",
                  padding: "12px 4px",
                  margin: 0,
                }}
              >
                {emptyLabel}
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
